import React, { useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useIntl } from 'react-intl';

import { useAnalyticsContext } from 'app/shared/utils';
import { ModalStep, OnSignUpSuccess, Ticket } from 'app/shared/typings';
import { useMultiStepSignUpContext } from 'app/shared/context';
import { AuthContext } from 'app/shared/context/Auth';
import { MultiStepSignUpWizard } from 'app/shared/components/organisms';
import { GetTicketLazy } from 'app/fan/graphql/tickets/queryHooks';
import { useEventPageContext } from 'app/fan/context/EventPage';
import { EventPageLoadingBlocks } from 'app/fan/components/pages/Event/components';

import { PlusOneTicketInfoForm } from './PlusOneTicketInfoForm';

const WizardSteps: { [key: string]: number } = {
  [ModalStep.TICKET_INFO]: 1,
  [ModalStep.COMPLETE_ACCOUNT]: 2,
  [ModalStep.SMS_OPT_IN]: 3,
};

export type PlusOneStatus = 'success' | 'error' | null;

interface Props {
  plusOneStatus: PlusOneStatus;
  setPlusOneStatus: React.Dispatch<React.SetStateAction<PlusOneStatus>>;
  isPlusOneModalOpen: boolean;
  setIsPlusOneModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const PlusOneOverlay: React.FC<React.PropsWithChildren<Props>> = ({
  plusOneStatus,
  setPlusOneStatus,
  isPlusOneModalOpen,
  setIsPlusOneModalOpen,
}) => {
  const { event, eventAttendee, ticketId } = useEventPageContext();
  const { formatMessage } = useIntl();
  const { trackAnalyticsEvent } = useAnalyticsContext();

  const { loggedIn, user: loggedInUser } = useContext(AuthContext);
  const { newUser, setNewUser } = useMultiStepSignUpContext();
  const user = loggedInUser || newUser;

  const shouldSkipToCompleteSignUp = !!ticketId;
  const initialStep =
    shouldSkipToCompleteSignUp || plusOneStatus === 'success'
      ? ModalStep.COMPLETE_ACCOUNT
      : ModalStep.TICKET_INFO;
  const [modalStep, setModalStep] = useState<ModalStep>(initialStep);

  const completedTicketInfoStep =
    shouldSkipToCompleteSignUp || plusOneStatus === 'success';
  const shouldSkipAccountSignup = completedTicketInfoStep && !!user;

  useEffect(() => {
    if (completedTicketInfoStep && shouldSkipAccountSignup) {
      setModalStep(ModalStep.SMS_OPT_IN);
    } else if (completedTicketInfoStep) {
      setModalStep(ModalStep.COMPLETE_ACCOUNT);
    }
  }, [completedTicketInfoStep]);

  const [userInfo, setUserInfo] = useState<UserInfo>({
    email: loggedInUser?.email,
    firstName: loggedInUser?.firstName,
    lastName: loggedInUser?.lastName,
  });

  const [loadTicket, { data: ticketData, loading: ticketDataLoading }] =
    GetTicketLazy();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (ticketId && eventAttendee?.id && eventAttendee) {
      loadTicket({
        variables: { id: ticketId, eventAttendeeId: Number(eventAttendee.id) },
      });
    }
  }, [ticketId, eventAttendee?.id, loadTicket]);

  useEffect(() => {
    if (shouldSkipToCompleteSignUp && !loggedIn && !!ticketData) {
      trackAnalyticsEvent('Plus One Flow Landed on Sign Up', {
        logged_in: false,
        email_has_account: !!ticketData?.ticket?.person?.user,
        trackOnce: true,
      });
    }
  }, [shouldSkipToCompleteSignUp, ticketData, loggedIn, trackAnalyticsEvent]);

  function onSubmitPlusOneTicketInfoSuccess(ticket?: Ticket) {
    setUserInfo({
      email: ticket?.email,
      firstName: ticket?.firstName,
      lastName: ticket?.lastName,
    });
    setNewUser(ticket?.person?.user);
    const user = loggedInUser || newUser;

    const oneHourFromNow = new Date(Date.now() + 60 * 60 * 1000);
    Cookies.set('customerLeadCreated', 'true', {
      expires: oneHourFromNow,
    });

    if (!ticket) {
      // skip remaining steps and go straight to error state
      trackAnalyticsEvent('Plus One Flow Error', {
        new_user: !newUser,
        error: 'Tickets Full',
      });
      setPlusOneStatus('error');
      setIsPlusOneModalOpen(false);
      return;
    }

    setPlusOneStatus('success');

    trackAnalyticsEvent('Plus One Flow Success', {
      logged_in: loggedIn,
      email_has_account: !!user,
      new_user: !user,
      is_guest_user: user?.isGuest,
    });
  }

  const setCompletedAccountCookie: OnSignUpSuccess = (signUpSuccessParams) => {
    if (signUpSuccessParams) {
      const expirationTime = 1 / 48;
      Cookies.set('completed_plus_one_flow_signup', 'true', {
        expires: expirationTime,
      });
    }
  };

  if (ticketDataLoading) {
    return <EventPageLoadingBlocks />;
  }

  if (!event || !eventAttendee) {
    return null;
  }

  const smsMarketingSignUpTitle = formatMessage({
    id: 'event.plusOneFlow.smsSignUp.title',
  });

  return (
    <MultiStepSignUpWizard
      isOpen={isPlusOneModalOpen}
      setIsOpen={setIsPlusOneModalOpen}
      formContext="plus-one"
      onSignUpSuccess={setCompletedAccountCookie}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      userEmail={userInfo?.email}
      stepOne={
        <PlusOneTicketInfoForm onSuccess={onSubmitPlusOneTicketInfoSuccess} />
      }
      smsMarketingSignUpTitle={smsMarketingSignUpTitle}
      activeStep={WizardSteps[modalStep]}
      modalStep={modalStep}
      setModalStep={setModalStep}
    />
  );
};

export default PlusOneOverlay;
